import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { doc, updateDoc, collection, addDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useTicket, useTicketMessages } from '@/hooks/useTickets';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { StatusBadge, TypeBadge, PriorityBadge } from '@/components/Badges';
import { timestampToDate, TicketStatus, TicketPriority, Attachment } from '@/types';

export function AdminTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { ticket, loading: ticketLoading, error: ticketError } = useTicket(id);
  const { messages, loading: messagesLoading } = useTicketMessages(id);
  
  const [replyContent, setReplyContent] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [ticketAttachments, setTicketAttachments] = useState<Attachment[]>([]);

  // Fetch ticket attachments
  useEffect(() => {
    const fetchAttachments = async () => {
      if (!id) return;
      try {
        const attachmentsSnap = await getDocs(collection(db, 'tickets', id, 'attachments'));
        const allAttachments: Attachment[] = [];
        attachmentsSnap.forEach((doc) => {
          const data = doc.data();
          if (data.attachments && Array.isArray(data.attachments)) {
            allAttachments.push(...data.attachments);
          }
        });
        setTicketAttachments(allAttachments);
      } catch (err) {
        console.error('Error fetching attachments:', err);
      }
    };
    fetchAttachments();
  }, [id]);

  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const MAX_TOTAL_SIZE = 20 * 1024 * 1024;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const oversized = newFiles.find(f => f.size > MAX_FILE_SIZE);
      if (oversized) {
        alert(`File "${oversized.name}" exceeds 5MB limit`);
        return;
      }
      const totalSize = [...files, ...newFiles].reduce((sum, f) => sum + f.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) {
        alert('Total attachment size exceeds 20MB limit');
        return;
      }
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (ticketId: string, messageId: string): Promise<Attachment[]> => {
    const attachments: Attachment[] = [];
    
    for (const file of files) {
      const storageRef = ref(storage, `tickets/${ticketId}/messages/${messageId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      attachments.push({
        name: file.name,
        url,
        size: file.size,
        type: file.type,
        uploadedAt: new Date() as any,
      });
    }
    
    return attachments;
  };

  const handleStatusChange = async (newStatus: TicketStatus) => {
    if (!id) return;
    try {
      await updateDoc(doc(db, 'tickets', id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        ...(newStatus === 'RESOLVED' && { resolvedAt: serverTimestamp() }),
      });
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const handlePriorityChange = async (newPriority: TicketPriority) => {
    if (!id) return;
    try {
      await updateDoc(doc(db, 'tickets', id), {
        priority: newPriority,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error updating priority:', err);
      alert('Failed to update priority');
    }
  };

  const handleAssignmentChange = async (userId: string | null) => {
    if (!id) return;
    try {
      await updateDoc(doc(db, 'tickets', id), {
        assignedToUserId: userId,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error updating assignment:', err);
      alert('Failed to update assignment');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this ticket? This action cannot be undone.'
    );
    
    if (!confirmDelete) return;
    
    try {
      const deleteFn = httpsCallable(functions, 'adminDeleteTicket');
      await deleteFn({ ticketId: id });
      navigate('/admin/tickets');
    } catch (err) {
      console.error('Error deleting ticket:', err);
      alert('Failed to delete ticket');
    }
  };

  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim() || !id || !currentUser || !userProfile) return;

    setSubmitting(true);
    try {
      // Upload files first if any
      let attachments: Attachment[] = [];
      if (files.length > 0) {
        const tempId = Date.now().toString();
        attachments = await uploadFiles(id, tempId);
      }

      const messageData: any = {
        authorId: currentUser.uid,
        authorEmail: currentUser.email!,
        authorName: userProfile.displayName || currentUser.email!.split('@')[0],
        authorRole: userProfile.role,
        content: replyContent.trim(),
        isInternal: isInternalNote,
        createdAt: serverTimestamp(),
      };

      if (attachments.length > 0) {
        messageData.attachments = attachments;
      }

      await addDoc(collection(db, 'tickets', id, 'messages'), messageData);

      // Bump ticket updatedAt
      await updateDoc(doc(db, 'tickets', id), {
        updatedAt: serverTimestamp(),
      });
      
      setReplyContent('');
      setIsInternalNote(false);
      setFiles([]);
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  if (ticketLoading) return <ProtectedRoute requireAdmin><Layout><LoadingSpinner /></Layout></ProtectedRoute>;

  if (ticketError || !ticket) {
    return (
      <ProtectedRoute requireAdmin><Layout>
        <div className="terminal-alert error">{ticketError ? 'Error loading ticket' : 'Ticket not found'}</div>
        <button onClick={() => navigate('/admin/tickets')} className="action-btn">Back to Admin</button>
      </Layout></ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requireAdmin>
      <Layout>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          {/* Back + Delete */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <button
              onClick={() => navigate('/admin/tickets')}
              className="topbar-link"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              ← Back to Admin
            </button>
            <button onClick={handleDelete} className="action-btn danger" style={{ minWidth: 'auto', padding: '6px 14px', fontSize: '9px' }}>
              Delete Ticket
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '12px', alignItems: 'start' }}>
            {/* Main column */}
            <div>
              {/* Ticket details */}
              <div className="terminal-card">
                <div className="card-header">
                  <span className="card-num" style={{ fontFamily: 'Consolas, monospace', fontSize: '9px' }}>
                    #{ticket.id.slice(-8).toUpperCase()}
                  </span>
                  <span className="card-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{ticket.title}</span>
                  <div className="card-header-right">
                    <StatusBadge status={ticket.status} />
                    <TypeBadge type={ticket.type} />
                    <PriorityBadge priority={ticket.priority} />
                    <span className="term-badge">{ticket.channel}</span>
                  </div>
                </div>
                <div className="card-body">
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span className="status-chip">FROM <strong>{ticket.ownerEmail}</strong></span>
                    <span className="status-chip">CREATED <strong>{timestampToDate(ticket.createdAt) ? format(timestampToDate(ticket.createdAt)!, 'dd MMM yyyy HH:mm') : 'NOW'}</strong></span>
                  </div>
                  <div>
                    <div className="field-label" style={{ marginBottom: '8px' }}>DESCRIPTION</div>
                    <div style={{
                      background: 'var(--surface-strong)', border: '1px solid var(--border)',
                      padding: '12px 14px', fontSize: '11px', color: 'var(--text)',
                      whiteSpace: 'pre-wrap', lineHeight: '1.6', textTransform: 'none',
                    }}>
                      {ticket.description}
                    </div>
                  </div>
                  {ticketAttachments.length > 0 && (
                    <div>
                      <div className="field-label" style={{ marginBottom: '8px' }}>ATTACHMENTS</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {ticketAttachments.map((attachment, idx) => (
                          <a key={idx} href={attachment.url} target="_blank" rel="noopener noreferrer" className="file-chip" style={{ textDecoration: 'none' }}>
                            {attachment.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="terminal-card">
                <div className="card-header">
                  <span className="card-num">02</span>
                  <span className="card-title">MESSAGES &amp; NOTES</span>
                  <div className="card-header-right">
                    <span className="stat-chip">COUNT <strong>{messages.length}</strong></span>
                  </div>
                </div>
                <div className="card-body">
                  {messagesLoading ? <LoadingSpinner /> : messages.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: 'var(--muted)', fontSize: '10px' }}>No messages yet</div>
                  ) : (
                    <div>
                      {messages.map((message) => {
                        const messageDate = timestampToDate(message.createdAt);
                        return (
                          <div key={message.id} className={`message-item${message.isInternal ? ' message-internal' : ''}`}>
                            <div className="message-meta">
                              <span className="message-author" style={{ color: message.authorRole === 'ADMIN' ? 'var(--gold)' : 'var(--success)' }}>
                                {message.authorName || message.authorEmail}
                              </span>
                              {message.authorRole === 'ADMIN' && (
                                <span className="term-badge medium" style={{ fontSize: '8px' }}>ADMIN</span>
                              )}
                              {message.isInternal && (
                                <span className="term-badge in-progress" style={{ fontSize: '8px' }}>INTERNAL</span>
                              )}
                              <span className="message-time">{messageDate ? format(messageDate, 'dd/MM/yy HH:mm') : 'NOW'}</span>
                            </div>
                            <div className="message-body" style={{ textTransform: 'none' }}>{message.content}</div>
                            {message.attachments && message.attachments.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                                {message.attachments.map((attachment, idx) => (
                                  <a key={idx} href={attachment.url} target="_blank" rel="noopener noreferrer" className="file-chip" style={{ textDecoration: 'none' }}>
                                    {attachment.name}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Reply */}
              <div className="terminal-card">
                <div className="card-header">
                  <span className="card-num">03</span>
                  <span className="card-title">SEND REPLY</span>
                </div>
                <div className="card-body">
                  <form onSubmit={handleReplySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      className="terminal-textarea"
                      placeholder="Type your message…"
                      required
                      style={{ textTransform: 'none', minHeight: '100px' }}
                    />
                    <div>
                      <input type="file" id="admin-reply-attachments" multiple onChange={handleFileChange} className="hidden" accept="image/*,.pdf,.txt,.log,.json,.zip" />
                      <label htmlFor="admin-reply-attachments" className="action-btn secondary" style={{ cursor: 'pointer', display: 'inline-flex', width: 'fit-content' }}>
                        Attach Files
                      </label>
                      {files.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                          {files.map((file, index) => (
                            <span key={index} className="file-chip">
                              {file.name}<button type="button" onClick={() => removeFile(index)}>Ã—</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '9px', color: 'var(--muted)', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={isInternalNote}
                          onChange={(e) => setIsInternalNote(e.target.checked)}
                          style={{ accentColor: 'var(--gold)' }}
                        />
                        Internal note (not visible to customer)
                      </label>
                      <button type="submit" disabled={submitting || !replyContent.trim()} className="action-btn large">
                        {submitting ? 'Sending…' : 'Send Reply'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              {/* Status/Priority management */}
              <div className="terminal-card">
                <div className="card-header">
                  <span className="card-num">S1</span>
                  <span className="card-title">MANAGE</span>
                </div>
                <div className="card-body">
                  <div className="field-group">
                    <label className="field-label">STATUS</label>
                    <select value={ticket.status} onChange={(e) => handleStatusChange(e.target.value as TicketStatus)} className="terminal-select">
                      <option value="OPEN">OPEN</option>
                      <option value="IN_PROGRESS">IN PROGRESS</option>
                      <option value="RESOLVED">RESOLVED</option>
                      <option value="CLOSED">CLOSED</option>
                    </select>
                  </div>
                  <div className="field-group">
                    <label className="field-label">PRIORITY</label>
                    <select value={ticket.priority} onChange={(e) => handlePriorityChange(e.target.value as TicketPriority)} className="terminal-select">
                      <option value="LOW">LOW</option>
                      <option value="MEDIUM">MEDIUM</option>
                      <option value="HIGH">HIGH</option>
                    </select>
                  </div>
                  <div className="field-group">
                    <label className="field-label">ASSIGNMENT</label>
                    {ticket.assignedToUserId ? (
                      <button onClick={() => handleAssignmentChange(null)} className="action-btn secondary" style={{ width: '100%', minWidth: 'auto' }}>
                        Unassign
                      </button>
                    ) : (
                      <button onClick={() => handleAssignmentChange(currentUser?.uid || null)} className="action-btn" style={{ width: '100%', minWidth: 'auto' }}>
                        Assign to Me
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer info */}
              <div className="terminal-card">
                <div className="card-header">
                  <span className="card-num">S2</span>
                  <span className="card-title">CUSTOMER</span>
                </div>
                <div className="card-body">
                  <div className="field-group">
                    <div className="field-label">EMAIL</div>
                    <div style={{ fontSize: '10px', color: 'var(--text)', wordBreak: 'break-all' }}>{ticket.ownerEmail}</div>
                  </div>
                  <div className="field-group">
                    <div className="field-label">USER ID</div>
                    <div style={{ fontSize: '8px', color: 'var(--muted)', fontFamily: 'Consolas, monospace', wordBreak: 'break-all' }}>{ticket.ownerId}</div>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div className="terminal-card">
                <div className="card-header">
                  <span className="card-num">S3</span>
                  <span className="card-title">METADATA</span>
                </div>
                <div className="card-body">
                  <div className="field-group">
                    <div className="field-label">TICKET ID</div>
                    <div style={{ fontSize: '8px', fontFamily: 'Consolas, monospace', color: 'var(--muted)', wordBreak: 'break-all' }}>{ticket.id}</div>
                  </div>
                  <div className="field-group">
                    <div className="field-label">CREATED</div>
                    <div style={{ fontSize: '9px', color: 'var(--text)' }}>
                      {timestampToDate(ticket.createdAt) ? format(timestampToDate(ticket.createdAt)!, 'dd/MM/yyyy HH:mm') : 'NOW'}
                    </div>
                  </div>
                  <div className="field-group">
                    <div className="field-label">UPDATED</div>
                    <div style={{ fontSize: '9px', color: 'var(--text)' }}>
                      {timestampToDate(ticket.updatedAt) ? format(timestampToDate(ticket.updatedAt)!, 'dd/MM/yyyy HH:mm') : 'NOW'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </ProtectedRoute>
  );
}
